import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuArrowLeftRight, LuInfo } from 'react-icons/lu';

import Modal from '../../../../components/Modal/Modal';
import { getDunningTransferTarget } from '../../../../services/finance/dunningApi';
import {
   CardHint,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   PrimaryActionButton,
   SecondaryButton,
   SkeletonBlock,
} from '../../../../styles/dunning/base';
import {
   ModalText,
   TargetBox,
   TransferList,
   TransferRow,
   TransferRowInfo,
   TransferRowSub,
   TransferRowTitle,
   TransferRowValue,
   TransferSelectionBar,
   TransferSelectionCount,
} from '../../../../styles/dunning/modals';
import { TRANSFER_MAX_CHARGES } from '../constants';
import { isExternalTrack } from '../utils/derive';
import { formatBRL, formatDateBR } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Transferência de títulos entre trilhas (handoff 0103 §5.1–5.3). Substituiu o
// ExternalModal, que só perguntava "tem certeza?" e escalava o CASO inteiro.
//
// O modelo mudou: escalar é mover TÍTULO, não caso. O caso enviado ao
// escritório continuava recebendo todo boleto que vencia depois — o título ia
// direto para o terceirizado sem nunca ser tentado pela casa. Por isso a
// seleção múltipla é o miolo do modal, e não um detalhe opcional.
//
// O MESMO componente serve os dois sentidos; a direção vem do backend
// (`transfer-target`), com a trilha do caso como fallback:
//   INTERNAL → EXTERNAL  = "Enviar ao escritório"  (POST /:id/escalate)
//   EXTERNAL → INTERNAL  = "Devolver à régua"      (POST /:id/return)
//
// `transfer-target` é chamado ao ABRIR, antes de qualquer escrita: é ele que
// responde "o cliente já tem caso na outra trilha?" e alimenta a confirmação
// que o Caio pediu ("este título será acrescido ao caso existente").
// ---------------------------------------------------------------------------

const COPY = {
   ESCALATE: {
      title: 'Enviar ao escritório',
      intro:
         'Os títulos selecionados saem da régua interna e passam a ser trabalhados pela cobrança administrativa. Os demais continuam em tratamento interno.',
      confirmLabel: 'Enviar títulos',
      sendingLabel: 'Enviando...',
      targetExisting: 'cobrança administrativa',
      targetNew:
         'Será criado um novo caso em cobrança administrativa para este cliente.',
   },
   RETURN: {
      title: 'Devolver à régua interna',
      intro:
         'Os títulos selecionados voltam a ser trabalhados internamente, com o histórico do caso que os receber.',
      confirmLabel: 'Devolver títulos',
      sendingLabel: 'Devolvendo...',
      targetExisting: 'régua interna',
      // Caso novo na devolução só acontece quando o ciclo anterior já terminou
      // (D21): com o episódio vivo, os títulos voltam para o caso interno que
      // já existe — ou retomam o estacionado, na raia dele.
      targetNew:
         'Será criado um novo caso na régua interna para este cliente, na raia de entrada.',
   },
};

export default function TransferChargesModal({
   isOpen,
   caseData = null,
   isSending,
   onClose,
   onConfirm,
}) {
   // Set de charge_id selecionados (o backend recebe ids de TÍTULO, não de
   // membro).
   const [selected, setSelected] = useState(() => new Set());
   const [target, setTarget] = useState(null);
   const [isLoadingTarget, setIsLoadingTarget] = useState(false);

   // Só título ABERTO atravessa: o backend responde 422 charge_not_in_dunning
   // para membro já baixado ou cancelado.
   const openMembers = useMemo(
      () =>
         (caseData?.members ?? []).filter(
            (member) => member.member_status === 'OPEN'
         ),
      [caseData]
   );

   // Direção autoritativa é a do backend; a trilha local cobre a janela em que
   // o `transfer-target` ainda não respondeu (ou falhou).
   const direction =
      target?.direction ?? (isExternalTrack(caseData) ? 'RETURN' : 'ESCALATE');
   const copy = COPY[direction] ?? COPY.ESCALATE;

   // Reabrir o modal recomeça do zero: nada de seleção herdada de outro caso.
   useEffect(() => {
      if (!isOpen || !caseData?.id) return undefined;

      let active = true;
      setSelected(new Set());
      setTarget(null);
      setIsLoadingTarget(true);

      getDunningTransferTarget(caseData.id)
         .then((result) => {
            if (active) setTarget(result);
         })
         .catch(() => {
            // Sem o contexto do destino o fluxo continua operável — o texto
            // cai no genérico e a confirmação segue valendo. Não vale bloquear
            // a transferência por uma leitura auxiliar.
         })
         .finally(() => {
            if (active) setIsLoadingTarget(false);
         });

      return () => {
         active = false;
      };
   }, [isOpen, caseData?.id]);

   const toggle = useCallback((chargeId) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(chargeId)) next.delete(chargeId);
         else next.add(chargeId);
         return next;
      });
   }, []);

   const allSelected =
      openMembers.length > 0 && selected.size === openMembers.length;

   const toggleAll = useCallback(() => {
      setSelected((prev) =>
         prev.size === openMembers.length
            ? new Set()
            : new Set(openMembers.map((member) => member.charge_id))
      );
   }, [openMembers]);

   const selectedAmount = openMembers.reduce(
      (acc, member) =>
         selected.has(member.charge_id) ? acc + Number(member.charge?.value ?? 0) : acc,
      0
   );

   // Teto do contrato (1..200). Só morde em caso gigante; sem o aviso o
   // operador levaria 422 depois de montar a seleção inteira.
   const overLimit = selected.size > TRANSFER_MAX_CHARGES;

   const handleConfirm = () => {
      if (!selected.size || overLimit) return;
      onConfirm({
         direction,
         chargeIds: Array.from(selected),
         // O caso de destino já existia? Quem chama usa isto para saber se
         // precisa levar o gestor ao seletor de responsável (§9).
         hadTarget: !!target?.target,
      });
   };

   const targetCase = target?.target ?? null;
   const targetOpenCount = targetCase?.open_count ?? 0;

   return (
      <Modal
         isOpen={isOpen}
         title={copy.title}
         titleIcon={<LuArrowLeftRight size={18} />}
         subtitle={caseData?.customer_name ?? null}
         onClose={onClose}
         maxWidth="560px"
         footer={
            <>
               <SecondaryButton type="button" onClick={onClose}>
                  Cancelar
               </SecondaryButton>
               <PrimaryActionButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={!selected.size || overLimit || isSending}
               >
                  {isSending ? copy.sendingLabel : copy.confirmLabel}
               </PrimaryActionButton>
            </>
         }
      >
         <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <ModalText>{copy.intro}</ModalText>

            {/* Contexto do destino — o "este título será acrescido ao caso
                existente do cliente" pedido no handoff. */}
            {isLoadingTarget ? (
               <SkeletonBlock $h="62px" $radius="12px" />
            ) : (
               <TargetBox>
                  <div
                     style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                     }}
                  >
                     <LuInfo
                        size={15}
                        style={{ flexShrink: 0, marginTop: 2 }}
                        color="#8a8a8a"
                     />
                     <div>
                        {/* Três desfechos possíveis, e a diferença importa: o
                            caso de destino pode já estar ativo, estar apenas
                            ESTACIONADO (D21 — esvaziou porque tudo migrou, mas
                            volta a receber título) ou não existir. */}
                        {!targetCase && copy.targetNew}
                        {targetCase && targetOpenCount > 0 && (
                           <>
                              O cliente já tem um caso em {copy.targetExisting}{' '}
                              com <b>{targetOpenCount}</b>{' '}
                              {targetOpenCount === 1 ? 'título' : 'títulos'} (
                              <b>{formatBRL(targetCase.open_amount)}</b>). Os
                              títulos selecionados serão acrescidos a ele.
                           </>
                        )}
                        {targetCase && targetOpenCount === 0 && (
                           <>
                              O cliente tem um caso <b>estacionado</b> em{' '}
                              {copy.targetExisting} (#{targetCase.id}). Os
                              títulos selecionados voltam para ele, na raia em
                              que parou — o histórico de tratativas é
                              preservado.
                           </>
                        )}
                        {targetCase && (
                           <div style={{ marginTop: 4, color: '#8a8a8a' }}>
                              Responsável:{' '}
                              {targetCase.assigned_user?.nome ??
                                 'sem responsável'}
                           </div>
                        )}
                     </div>
                  </div>
               </TargetBox>
            )}

            {openMembers.length === 0 ? (
               <EmptyWrap style={{ padding: '24px 8px' }}>
                  <EmptyTitle>Nenhum título em aberto</EmptyTitle>
                  <EmptyText>
                     Só títulos em aberto podem mudar de trilha.
                  </EmptyText>
               </EmptyWrap>
            ) : (
               <>
                  <TransferSelectionBar>
                     <TransferSelectionCount>
                        {selected.size} de {openMembers.length}
                     </TransferSelectionCount>
                     <span>
                        selecionados · {formatBRL(selectedAmount)}
                     </span>
                     <div style={{ flex: 1 }} />
                     <SecondaryButton
                        type="button"
                        style={{ height: 32, fontSize: 12 }}
                        onClick={toggleAll}
                     >
                        {allSelected ? 'Limpar seleção' : 'Selecionar todos'}
                     </SecondaryButton>
                  </TransferSelectionBar>

                  <TransferList>
                     {openMembers.map((member) => {
                        const isSelected = selected.has(member.charge_id);
                        return (
                           <TransferRow
                              key={member.charge_id}
                              $selected={isSelected}
                           >
                              <input
                                 type="checkbox"
                                 checked={isSelected}
                                 onChange={() => toggle(member.charge_id)}
                              />
                              <TransferRowInfo>
                                 <TransferRowTitle>
                                    {member.charge?.installment_number
                                       ? `Parcela ${member.charge.installment_number}/${member.charge.installment_count}`
                                       : 'Título avulso'}
                                 </TransferRowTitle>
                                 <TransferRowSub>
                                    {`venc. ${formatDateBR(
                                       member.charge?.due_date
                                    )}`}
                                    {member.charge?.order_number
                                       ? ` · pedido ${member.charge.order_number}`
                                       : ''}
                                 </TransferRowSub>
                              </TransferRowInfo>
                              <TransferRowValue>
                                 {formatBRL(member.charge?.value)}
                              </TransferRowValue>
                           </TransferRow>
                        );
                     })}
                  </TransferList>

                  {overLimit && (
                     <CardHint style={{ color: '#a01b1b' }}>
                        Máximo de {TRANSFER_MAX_CHARGES} títulos por
                        transferência. Faça em lotes menores.
                     </CardHint>
                  )}
               </>
            )}
         </div>
      </Modal>
   );
}

TransferChargesModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // Caso COMPLETO (GET /:id) — precisa dos `members` para listar os títulos.
   // eslint-disable-next-line react/forbid-prop-types
   caseData: PropTypes.object,
   isSending: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
